import React, { forwardRef } from 'react';
import clsx from 'clsx';
import { capitalize } from '@material-ui/core/utils';
import MuiButton from '@material-ui/core/Button';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import type { PropTypes } from '@material-ui/core';
import type { ButtonProps, OverridableButton } from './types';
import useStyles from './styles';

const Button = forwardRef(function Button<C extends React.ElementType>(
    props: ButtonProps<C, { component?: C }>,
    ref: React.ComponentPropsWithRef<C>,
): JSX.Element {
    const {
        children,
        className,
        color = 'default',
        disabled,
        disableGutters = false,
        loading,
        shape = 'rounded',
        size,
        variant = 'text',
        ...rest
    } = props;

    const innerColor = ['inherit', 'primary', 'secondary'].includes(color) ? (color as PropTypes.Color) : 'default';

    const classes = useStyles();

    type TClasses = keyof typeof classes;

    return (
        <MuiButton
            ref={ref}
            size={size}
            color={innerColor}
            variant={variant}
            disabled={disabled || loading}
            className={clsx(
                classes.root,
                classes[variant],
                {
                    [classes.disableGutters]: disableGutters,
                    [classes.circular]: shape === 'circular',
                    [classes[`${variant}${capitalize(String(color))}` as TClasses]]:
                        color !== 'default' && color !== 'inherit' && color !== 'primary' && color !== 'secondary',
                    [classes[`circularSize${capitalize(String(size))}` as TClasses]]:
                        shape === 'circular' && (size === 'small' || size === 'large'),
                },
                className,
            )}
            {...rest}
        >
            {loading ? <div className={classes.labelHidden}>{children}</div> : children}
            {loading && <MuiCircularProgress size={20} className={classes.progress} />}
        </MuiButton>
    );
});

Button.defaultProps = {
    color: 'default',
};

export type { ButtonProps };
export default Button as OverridableButton;
